import React, { useState, useEffect, useRef } from 'react';
import '../styles/DashboardOverview.css';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Doughnut, Bar } from 'react-chartjs-2';
import {
  fetchGraphAnalytics,
  fetchCentrality,
  fetchSavedInsights,
  getEntityContext,
} from '../services/dashboardService';
import EntityContextWindow from './EntityContextWindow';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const GraphAnalytics = ({ onNavigate } = {}) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(false);

  // Overview Tab State
  const [analytics, setAnalytics] = useState(null);

  // Centrality Tab State
  const [centralityData, setCentralityData] = useState([]);
  const [centralityLimit, setCentralityLimit] = useState(20);
  const [isDark, setIsDark] = useState(document.documentElement.getAttribute('data-theme') === 'dark');

  // Refs to track Chart.js instances for cleanup
  const chartInstancesRef = useRef({ doughnut: null, bar: null });

  // Listen for theme changes
  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'data-theme') {
          setIsDark(document.documentElement.getAttribute('data-theme') === 'dark');
        }
      });
    });
    
    observer.observe(document.documentElement, { attributes: true });
    
    return () => {
      console.log('[GraphAnalytics] Cleaning up theme observer');
      observer.disconnect();
    };
  }, []);

  // Comprehensive cleanup on unmount
  useEffect(() => {
    return () => {
      console.log('[GraphAnalytics] Component unmounting - destroying charts and clearing state');
      
      // Destroy Chart.js instances
      if (chartInstancesRef.current.doughnut) {
        chartInstancesRef.current.doughnut.destroy();
        chartInstancesRef.current.doughnut = null;
      }
      if (chartInstancesRef.current.bar) {
        chartInstancesRef.current.bar.destroy();
        chartInstancesRef.current.bar = null;
      }
      
      // Clear all state to release references
      setAnalytics(null);
      setCentralityData([]);
      setSavedInsights([]);
      setExpandedInsights(new Set());
    };
  }, []);

  const categoryColors = {
    COMPANY: isDark ? '#ff2d49' : '#4B9DA9',
    PERSON: isDark ? '#e5edee' : '#0046FF',
    LOCATION: isDark ? '#3134ff' : '#73C8D2',
    AUCTION: isDark ? '#51f34bf8' : '#d97706',
    DOCUMENT: isDark ? '#6AECE1' : '#26CCC2',
    PROPERTY: isDark ? '#c300ff' : '#db2777',
    OTHER: isDark ? '#ffee00' : '#475569'
  };

  // Insights Tab State
  const [savedInsights, setSavedInsights] = useState([]);
  const insightsPage = 0; // pagination placeholder (first page only for now)
  const [expandedInsights, setExpandedInsights] = useState(new Set());
  const [insightSort, setInsightSort] = useState({ field: 'date', direction: 'desc' });
  const [insightFilter, setInsightFilter] = useState('all');

  // Lead Modal State
  const [selectedLead, setSelectedLead] = useState(null); // the full insight object
  const [ecwEntityId, setEcwEntityId] = useState(null);   // entity UUID to open ECW over the lead modal

  // Load data based on active tab
  useEffect(() => {
    if (activeTab === 'overview') {
      loadAnalytics();
    } else if (activeTab === 'centrality') {
      loadCentrality();
    } else if (activeTab === 'insights') {
      loadSavedInsights();
    }
  }, [activeTab]);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const result = await fetchGraphAnalytics();
      if (result.success) {
        setAnalytics(result.data);
      }
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCentrality = async () => {
    setLoading(true);
    try {
      const result = await fetchCentrality(centralityLimit);
      console.log('Centrality API Response:', result);
      
      if (result.success) {
        // Handle various response structures
        let data = [];
        
        if (Array.isArray(result.data)) {
          data = result.data;
        } else if (result.data?.entities) {
          data = result.data.entities;
        } else if (result.data?.topEntities) {
          data = result.data.topEntities;
        } else if (typeof result.data === 'object') {
          // If data is an object, convert its values to array
          data = Object.values(result.data);
        }
        
        console.log('Processed centrality data:', data);
        setCentralityData(data);
      }
    } catch (error) {
      console.error('Error loading centrality:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSavedInsights = async () => {
    setLoading(true);
    try {
      // Fetch all saved insights (includes whiteboard_analytics, entity_summary, cross_domain_lead, etc.)
      const kgResult = await fetchSavedInsights(null, 20, insightsPage * 20);

      let insights = [];

      if (kgResult.success) {
        if (Array.isArray(kgResult.data)) {
          insights = kgResult.data;
        } else if (kgResult.data?.insights) {
          insights = kgResult.data.insights;
        } else if (kgResult.data?.data) {
          insights = kgResult.data.data;
        }
      }

      // ── Enrich entity_summary insights with resolved entity names ──────────
      // The insight record only stores entityIds (UUIDs); we fetch entity names
      // in parallel so the source badge can show a human-readable name.
      const entityInsights = insights.filter(
        (i) => i.insightType === 'entity_summary' && !i.entityName && i.entityIds?.[0]
      );
      if (entityInsights.length > 0) {
        const uniqueIds = [...new Set(entityInsights.map((i) => i.entityIds[0]))].slice(0, 20);
        const nameMap = {};
        await Promise.all(
          uniqueIds.map(async (id) => {
            try {
              const res = await getEntityContext(id);
              if (res.success && res.data?.entity?.entity_name) {
                nameMap[id] = res.data.entity.entity_name;
              }
            } catch {
              // silent — badge will show generic 'Entity' fallback
            }
          })
        );
        insights = insights.map((i) => {
          if (
            i.insightType === 'entity_summary' &&
            i.entityIds?.[0] &&
            nameMap[i.entityIds[0]]
          ) {
            return { ...i, entityName: nameMap[i.entityIds[0]] };
          }
          return i;
        });
      }

      setSavedInsights(insights);
    } catch (error) {
      console.error('Error loading saved insights:', error);
    } finally {
      setLoading(false);
    }
  };

  // Chart data transformation functions
  const getEntityChartData = () => {
    if (!analytics?.entityTypeDistribution || analytics.entityTypeDistribution.length === 0) {
      return null;
    }

    const sortedData = analytics.entityTypeDistribution
      .sort((a, b) => b.count - a.count);

    const labels = sortedData.map(item => item.entity_type);
    const data = sortedData.map(item => item.count);
    const colors = sortedData.map(item => {
      const type = item.entity_type.toUpperCase();
      return categoryColors[type] || categoryColors.OTHER;
    });

    return {
      labels,
      datasets: [
        {
          data,
          backgroundColor: colors,
          borderColor: colors.map(color => color),
          borderWidth: 2,
          hoverOffset: 10,
        },
      ],
    };
  };

  const getRelationshipChartData = () => {
    if (!analytics?.relationshipTypeDistribution || analytics.relationshipTypeDistribution.length === 0) {
      return null;
    }

    const sortedData = analytics.relationshipTypeDistribution
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);

    const labels = sortedData.map(item => item.relationship_type);
    const data = sortedData.map(item => item.count);

    // Generate gradient colors from primary color
    const primaryColor = isDark ? '#3b82f6' : '#2563eb';
    const colors = sortedData.map((_, index) => {
      const opacity = 0.9 - (index * 0.06);
      return `${primaryColor}${Math.round(opacity * 255).toString(16).padStart(2, '0')}`;
    });

    return {
      labels,
      datasets: [
        {
          label: 'Count',
          data,
          backgroundColor: colors,
          borderColor: primaryColor,
          borderWidth: 1,
          borderRadius: 6,
        },
      ],
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          color: isDark ? '#f4f4f5' : '#18181b',
          padding: 15,
          font: {
            size: 12,
            weight: 500,
          },
          usePointStyle: true,
          pointStyle: 'circle',
        },
      },
      tooltip: {
        backgroundColor: isDark ? '#27272a' : '#ffffff',
        titleColor: isDark ? '#f4f4f5' : '#18181b',
        bodyColor: isDark ? '#a1a1aa' : '#71717a',
        borderColor: isDark ? '#3f3f46' : '#e4e4e7',
        borderWidth: 1,
        padding: 12,
        displayColors: true,
        callbacks: {
          label: function(context) {
            const label = context.label || '';
            const value = context.parsed || context.parsed === 0 ? context.parsed : '';
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = ((value / total) * 100).toFixed(1);
            return `${label}: ${value} (${percentage}%)`;
          }
        }
      },
    },
  };

  const barChartOptions = {
    ...chartOptions,
    indexAxis: 'y',
    scales: {
      x: {
        beginAtZero: true,
        ticks: {
          color: isDark ? '#a1a1aa' : '#71717a',
          font: {
            size: 11,
          },
        },
        grid: {
          color: isDark ? '#27272a' : '#f4f4f5',
        },
      },
      y: {
        ticks: {
          color: isDark ? '#a1a1aa' : '#71717a',
          font: {
            size: 11,
          },
        },
        grid: {
          display: false,
        },
      },
    },
    plugins: {
      ...chartOptions.plugins,
      legend: {
        display: false,
      },
    },
  };

  // Cleanup charts when switching tabs — always destroy on cleanup since
  // the closure captures the OLD activeTab value (stale closure fix)
  useEffect(() => {
    return () => {
      console.log('[GraphAnalytics] Tab changing - destroying chart instances');
      if (chartInstancesRef.current.doughnut) {
        chartInstancesRef.current.doughnut.destroy();
        chartInstancesRef.current.doughnut = null;
      }
      if (chartInstancesRef.current.bar) {
        chartInstancesRef.current.bar.destroy();
        chartInstancesRef.current.bar = null;
      }
    };
  }, [activeTab]);

  const renderOverviewTab = () => {
    if (loading) return <div className="loading">Loading analytics...</div>;
    if (!analytics) return <div className="no-data">No analytics data available</div>;

    const entityChartData = getEntityChartData();
    const relationshipChartData = getRelationshipChartData();

    return (
      <div className="analytics-overview">
        <div className="metrics-grid">
          <div className="metric-card">
            <div className="metric-icon"><i className="fas fa-circle-nodes"></i></div>
            <div className="metric-content">
              <div className="metric-label">Total Nodes</div>
              <div className="metric-value">{analytics.totalNodes || 0}</div>
            </div>
          </div>

          <div className="metric-card">
            <div className="metric-icon"><i className="fas fa-link"></i></div>
            <div className="metric-content">
              <div className="metric-label">Total Edges</div>
              <div className="metric-value">{analytics.totalEdges || 0}</div>
            </div>
          </div>

          <div className="metric-card">
            <div className="metric-icon"><i className="fas fa-project-diagram"></i></div>
            <div className="metric-content">
              <div className="metric-label">Graph Density</div>
              <div className="metric-value">{(analytics.graphDensity || 0).toFixed(4)}</div>
            </div>
          </div>

          <div className="metric-card">
            <div className="metric-icon"><i className="fas fa-diagram-project"></i></div>
            <div className="metric-content">
              <div className="metric-label">Avg Connections</div>
              <div className="metric-value">{(analytics.averageDegree || 0).toFixed(2)}</div>
            </div>
          </div>
        </div>

        <div className="charts-section">
          <div className="chart-card">
            <div className="chart-header">
              <h3><i className="fas fa-tags"></i> Entity Type Distribution</h3>
            </div>
            <div className="chart-container">
              {entityChartData ? (
                <Doughnut 
                  data={entityChartData} 
                  options={chartOptions}
                  ref={(ref) => {
                    if (ref) {
                      chartInstancesRef.current.doughnut = ref;
                    }
                  }}
                />
              ) : (
                <div className="no-data">No entity distribution data</div>
              )}
            </div>
          </div>

          <div className="chart-card">
            <div className="chart-header">
              <h3><i className="fas fa-arrow-right-arrow-left"></i> Relationship Type Distribution</h3>
              <p className="chart-subtitle">Top 10 Relationships</p>
            </div>
            <div className="chart-container chart-container-bar">
              {relationshipChartData ? (
                <Bar 
                  data={relationshipChartData} 
                  options={barChartOptions}
                  ref={(ref) => {
                    if (ref) {
                      chartInstancesRef.current.bar = ref;
                    }
                  }}
                />
              ) : (
                <div className="no-data">No relationship distribution data</div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderCentralityTab = () => {
    if (loading) return <div className="loading">Loading centrality data...</div>;

    return (
      <div className="centrality-container">
        <div className="centrality-header">
          <h3><i className="fas fa-star"></i> Top Connected Entities</h3>
          <div className="centrality-controls">
            <label>Show rows:</label>
            <select 
              value={centralityLimit} 
              onChange={(e) => {
                setCentralityLimit(parseInt(e.target.value));
                setTimeout(() => loadCentrality(), 100);
              }}
              className="centrality-limit-select"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </select>
          </div>
        </div>

        {centralityData.length === 0 ? (
          <div className="no-data">No centrality data available</div>
        ) : (
          <div className="centrality-table">
            <table>
              <thead>
                <tr>
                  <th width="60">Rank</th>
                  <th>Entity</th>
                  <th width="120">Type</th>
                  <th width="100" className="text-center">Total</th>
                  <th width="100" className="text-center">In</th>
                  <th width="100" className="text-center">Out</th>
                </tr>
              </thead>
              <tbody>
                {centralityData.slice(0, centralityLimit).map((entity, index) => {
                  const type = (entity.entity_type || entity.type || 'OTHER').toUpperCase();
                  const color = categoryColors[type] || categoryColors.OTHER;
                  
                  return (
                    <tr key={entity.id}>
                      <td className="rank-cell">#{index + 1}</td>
                      <td className="entity-cell">
                        <strong>{entity.entity_name || entity.label}</strong>
                      </td>
                      <td className="type-cell">
                        <span 
                          className="type-badge" 
                          style={{ 
                            backgroundColor: `${color}20`, // 20% opacity
                            color: color,
                            border: `1px solid ${color}40` // 40% opacity border
                          }}
                        >
                          {type}
                        </span>
                      </td>
                      <td className="degree-cell text-center">
                        <strong>{entity.degree || entity.totalDegree || 0}</strong>
                      </td>
                      <td className="text-center">{entity.incomingConnections || 0}</td>
                      <td className="text-center">{entity.outgoingConnections || 0}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  const renderInsightsTab = () => {
    // ── Helpers ──────────────────────────────────────────────────────────────
    const getSummary = (ins) =>
      ins?.summary || ins?.board_summary?.overview || '';

    const getSourceLabel = (item) => {
      if (item.insightType === 'whiteboard_analytics') return item.boardName || 'Board';
      if (item.insightType === 'entity_summary') {
        // Try all common places where the entity name might be stored;
        // never fall back to the raw UUID from entityIds
        return (
          item.entityName ||
          item.entity_name ||
          item.insight?.subject ||
          item.insight?.entity_name ||
          item.insight?.entityName ||
          item.insight?.metadata?.entity_name ||
          item.insight?.metadata?.name ||
          'Entity'
        );
      }
      if (item.insightType === 'relationship_analysis') return 'Relationship';
      if (item.insightType === 'cross_domain_lead') {
        return item.insight?.hub_entity || item.insight?.lead_type || 'Cross Domain Lead';
      }
      return 'Knowledge Graph';
    };

    const handleViewInsight = (item) => {
      if (item.insightType === 'cross_domain_lead') {
        setSelectedLead(item);
        return;
      }
      if (!onNavigate) return;
      if (item.insightType === 'whiteboard_analytics' && item.boardId) {
        onNavigate('whiteboard', { boardId: item.boardId });
      } else if (item.insightType === 'entity_summary') {
        const entityId = item.entityIds?.[0] || item.entityId;
        onNavigate('knowledge-graph', { entityId, entityName: getSourceLabel(item) });
      } else {
        onNavigate('knowledge-graph', {});
      }
    };

    const getNavBtnContent = (item) => {
      if (item.insightType === 'whiteboard_analytics')
        return <><i className="fas fa-chalkboard"></i> View Board</>;
      if (item.insightType === 'entity_summary')
        return <><i className="fas fa-user-circle"></i> View Entity</>;
      if (item.insightType === 'cross_domain_lead')
        return <><i className="fas fa-bullseye"></i> View Lead</>;
      return <><i className="fas fa-circle-nodes"></i> View Graph</>;
    };

    // ── Filter + sort ─────────────────────────────────────────────────────────
    const filteredInsights = savedInsights
      .filter(item => insightFilter === 'all' || item.insightType === insightFilter)
      .sort((a, b) => {
        const dateA = new Date(a.createdAt || a.generatedAt);
        const dateB = new Date(b.createdAt || b.generatedAt);
        return insightSort.direction === 'desc' ? dateB - dateA : dateA - dateB;
      });

    const toggleInsight = (id) => {
      setExpandedInsights(prev => {
        const next = new Set(prev);
        if (next.has(id)) { next.delete(id); } else { next.add(id); }
        return next;
      });
    };

    return (
      <div className="insights-container">
        <div className="insights-history-section">
          {/* ── Header row ── */}
          <div className="insights-header">
            <div className="insights-title-row">
              <div>
                <h3><i className="fas fa-lightbulb"></i> AI Insights Collection</h3>
                <p className="insights-description">
                  All AI-generated analyses — entity summaries, network patterns, and investigation board insights.
                </p>
              </div>
              <div className="insights-controls">
                <select
                  value={insightFilter}
                  onChange={(e) => setInsightFilter(e.target.value)}
                  className="insight-filter-select"
                >
                  <option value="all">All Types</option>
                  <option value="cross_domain_lead">Cross Domain Leads</option>
                  <option value="network_patterns">Network Patterns</option>
                  <option value="relationship_analysis">Relationship Analysis</option>
                  <option value="entity_summary">Entity Summary</option>
                  <option value="whiteboard_analytics">Board Insights</option>
                </select>
                <button
                  className="sort-btn"
                  onClick={() => setInsightSort(prev => ({
                    ...prev,
                    direction: prev.direction === 'desc' ? 'asc' : 'desc',
                  }))}
                  title="Sort by Date"
                >
                  <i className={`fas fa-sort-numeric-${insightSort.direction === 'desc' ? 'down' : 'up'}`}></i>
                </button>
              </div>
            </div>
          </div>

          {/* ── Content ── */}
          {loading ? (
            <div className="loading">Loading insights…</div>
          ) : filteredInsights.length === 0 ? (
            <div className="no-data">
              <i className="fas fa-info-circle"></i>
              <p>No insights found.</p>
              {insightFilter === 'all' && (
                <ul className="insight-tips">
                  <li><i className="fas fa-user"></i> Click an entity in the Knowledge Graph and select "Generate Summary"</li>
                  <li><i className="fas fa-chalkboard"></i> Open a Whiteboard and click "Insights" to analyse a board</li>
                  <li><i className="fas fa-code-compare"></i> Compare two entities and click "Analyze Relationship"</li>
                </ul>
              )}
            </div>
          ) : (
            <div className="insights-list">
              {filteredInsights.map((insight) => {
                const isExpanded = expandedInsights.has(insight.id);
                const date = new Date(insight.createdAt || insight.generatedAt).toLocaleString();
                const typeLabel = insight.insightType?.replace(/_/g, ' ');
                const summary = getSummary(insight.insight);
                const isWhiteboard = insight.insightType === 'whiteboard_analytics';
                const isCrossDomainLead = insight.insightType === 'cross_domain_lead';
                const leadScore = isCrossDomainLead ? insight.insight?.lead_score : null;

                return (
                  <div key={insight.id} className={`insight-card ${isExpanded ? 'expanded' : ''} ${isCrossDomainLead ? 'insight-card--lead' : ''}`}>
                    {/* ── Card header — always visible ── */}
                    <div
                      className="insight-card-header clickable"
                      onClick={() => toggleInsight(insight.id)}
                    >
                      <div className="insight-header-main">
                        {/* Type badge */}
                        <span className={`insight-type-badge ${insight.insightType}`}>
                          {isWhiteboard && <i className="fas fa-chalkboard" style={{ marginRight: 4 }}></i>}
                          {isCrossDomainLead && <i className="fas fa-bullseye" style={{ marginRight: 4 }}></i>}
                          {typeLabel}
                        </span>
                        {/* Lead score badge for cross_domain_lead */}
                        {isCrossDomainLead && leadScore != null && (
                          <span className={`lead-score-badge lead-score-${leadScore >= 8 ? 'high' : leadScore >= 5 ? 'med' : 'low'}`}>
                            <i className="fas fa-star"></i> {leadScore}/10
                          </span>
                        )}
                        {/* Source context badge */}
                        <span className="insight-source-badge">
                          {isWhiteboard
                            ? <><i className="fas fa-layer-group"></i> {getSourceLabel(insight)}</>
                            : insight.insightType === 'entity_summary'
                              ? <><i className="fas fa-user-circle"></i> {getSourceLabel(insight)}</>
                              : isCrossDomainLead
                                ? <><i className="fas fa-bullseye"></i> {getSourceLabel(insight)}</>
                                : <><i className="fas fa-circle-nodes"></i> {getSourceLabel(insight)}</>
                          }
                        </span>
                        {/* Summary preview */}
                        <span className="insight-summary-preview">
                          {summary
                            ? summary.substring(0, 110) + (summary.length > 110 ? '…' : '')
                            : '—'}
                        </span>
                      </div>
                      <div className="insight-header-meta">
                        <span className="insight-time">{date}</span>
                        <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'}`}></i>
                      </div>
                    </div>

                    {/* ── Expanded body ── */}
                    {isExpanded && insight.insight && (
                      <div className="insight-card-content">

                        {/* Navigate to source button */}
                        {(onNavigate || isCrossDomainLead) && (
                          <div className="insight-nav-row">
                            <button
                              className={`insight-nav-btn ${isCrossDomainLead ? 'insight-nav-btn--lead' : ''}`}
                              onClick={(e) => { e.stopPropagation(); handleViewInsight(insight); }}
                            >
                              {getNavBtnContent(insight)}
                            </button>
                          </div>
                        )}

                        {/* Summary */}
                        {summary && (
                          <div className="insight-full-summary">
                            <h4>Summary</h4>
                            <p>{summary}</p>
                          </div>
                        )}

                        {/* KG-style: highlights */}
                        {insight.insight.highlights?.length > 0 && (
                          <div className="insight-section">
                            <h4><i className="fas fa-star"></i> Key Highlights</h4>
                            <ul>
                              {insight.insight.highlights.map((h, i) => <li key={i}>{h}</li>)}
                            </ul>
                          </div>
                        )}

                        {/* Whiteboard-style: board_summary */}
                        {insight.insight.board_summary && (
                          <div className="insight-section">
                            <h4><i className="fas fa-chart-pie"></i> Board Summary</h4>
                            {insight.insight.board_summary.themes?.length > 0 && (
                              <div className="insight-sub-group">
                                <span className="insight-sub-label"><i className="fas fa-tags"></i> Themes</span>
                                <ul>{insight.insight.board_summary.themes.map((t, i) => <li key={i}>{t}</li>)}</ul>
                              </div>
                            )}
                            {insight.insight.board_summary.market_signals?.length > 0 && (
                              <div className="insight-sub-group">
                                <span className="insight-sub-label"><i className="fas fa-signal"></i> Market Signals</span>
                                <ul>{insight.insight.board_summary.market_signals.map((s, i) => <li key={i}>{s}</li>)}</ul>
                              </div>
                            )}
                            {insight.insight.board_summary.anomalies?.length > 0 && (
                              <div className="insight-sub-group">
                                <span className="insight-sub-label"><i className="fas fa-exclamation-triangle"></i> Anomalies</span>
                                <ul>{insight.insight.board_summary.anomalies.map((a, i) => <li key={i}>{a}</li>)}</ul>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Whiteboard-style: connection_analysis */}
                        {insight.insight.connection_analysis && (
                          <div className="insight-section">
                            <h4><i className="fas fa-project-diagram"></i> Connection Analysis</h4>
                            {insight.insight.connection_analysis.key_relationships?.length > 0 && (
                              <div className="insight-sub-group">
                                <span className="insight-sub-label"><i className="fas fa-link"></i> Key Relationships</span>
                                <ul>{insight.insight.connection_analysis.key_relationships.map((r, i) => <li key={i}>{r}</li>)}</ul>
                              </div>
                            )}
                            {insight.insight.connection_analysis.hub_items?.length > 0 && (
                              <div className="insight-sub-group">
                                <span className="insight-sub-label"><i className="fas fa-star"></i> Hub Items</span>
                                <ul>{insight.insight.connection_analysis.hub_items.map((h, i) => <li key={i}>{h}</li>)}</ul>
                              </div>
                            )}
                            {insight.insight.connection_analysis.investigation_leads?.length > 0 && (
                              <div className="insight-sub-group">
                                <span className="insight-sub-label"><i className="fas fa-search"></i> Investigation Leads</span>
                                <ul>{insight.insight.connection_analysis.investigation_leads.map((l, i) => <li key={i}>{l}</li>)}</ul>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Recommendations (shared by all types) */}
                        {insight.insight.recommendations?.length > 0 && (
                          <div className="insight-section">
                            <h4><i className="fas fa-lightbulb"></i> Recommendations</h4>
                            <ul>
                              {insight.insight.recommendations.map((r, i) => <li key={i}>{r}</li>)}
                            </ul>
                          </div>
                        )}

                        {/* Risk Factors (KG-style) */}
                        {insight.insight.riskFactors?.length > 0 && (
                          <div className="insight-section risk">
                            <h4><i className="fas fa-exclamation-triangle"></i> Risk Factors</h4>
                            <ul>
                              {insight.insight.riskFactors.map((r, i) => <li key={i}>{r}</li>)}
                            </ul>
                          </div>
                        )}

                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  };

  // ── Cross Domain Lead Modal ───────────────────────────────────────────────
  const renderLeadModal = () => {
    if (!selectedLead) return null;
    const ins = selectedLead.insight || {};
    const entityIds = selectedLead.entityIds || [];

    return (
      <LeadModal
        ins={ins}
        entityIds={entityIds}
        onClose={() => setSelectedLead(null)}
        onOpenEntity={(id) => setEcwEntityId(id)}
      />
    );
  };

  return (
    <div className="graph-analytics-container">
      <div className="analytics-tabs">
        <button
          className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          <i className="fas fa-chart-pie"></i> Overview
        </button>
        <button
          className={`tab-btn ${activeTab === 'insights' ? 'active' : ''}`}
          onClick={() => setActiveTab('insights')}
        >
          <i className="fas fa-lightbulb"></i> AI Insights
        </button>
        <button
          className={`tab-btn ${activeTab === 'centrality' ? 'active' : ''}`}
          onClick={() => setActiveTab('centrality')}
        >
          <i className="fas fa-star"></i> Top Entities
        </button>
      </div>

      <div className="analytics-content">
        {activeTab === 'overview' && renderOverviewTab()}
        {activeTab === 'insights' && renderInsightsTab()}
        {activeTab === 'centrality' && renderCentralityTab()}
      </div>

      {/* Lead Modal */}
      {renderLeadModal()}

      {/* EntityContextWindow — floats on top of lead modal */}
      {ecwEntityId && (
        <EntityContextWindow
          entityId={ecwEntityId}
          onClose={() => setEcwEntityId(null)}
        />
      )}
    </div>
  );
};

// ── LeadModal sub-component ───────────────────────────────────────────────────

const LeadModal = ({ ins, entityIds, onClose, onOpenEntity }) => {
  const [entityNames, setEntityNames] = useState({}); // id → { name, type }
  const [loadingEntities, setLoadingEntities] = useState(false);

  // Fetch entity names/types for all entity_ids
  useEffect(() => {
    if (!entityIds || entityIds.length === 0) return;
    let cancelled = false;
    setLoadingEntities(true);
    Promise.all(
      entityIds.map(async (id) => {
        try {
          const res = await getEntityContext(id);
          if (!cancelled && res.success && res.data?.entity) {
            return { id, name: res.data.entity.entity_name, type: res.data.entity.entity_type };
          }
        } catch { /* silent */ }
        return { id, name: null, type: null };
      })
    ).then((results) => {
      if (!cancelled) {
        const map = {};
        results.forEach(({ id, name, type }) => { map[id] = { name, type }; });
        setEntityNames(map);
        setLoadingEntities(false);
      }
    });
    return () => { cancelled = true; };
  }, [entityIds]);

  // Escape key closes
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const score = ins.lead_score ?? null;
  const scoreClass = score >= 8 ? 'high' : score >= 5 ? 'med' : 'low';

  return (
    <div className="lead-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="lead-modal" role="dialog" aria-modal="true">

        {/* ── Header ── */}
        <div className="lead-modal-header">
          <div className="lead-modal-header-left">
            <span className="lead-modal-type-chip">
              <i className="fas fa-bullseye"></i> Cross Domain Lead
            </span>
            {ins.lead_type && (
              <h2 className="lead-modal-title">{ins.lead_type}</h2>
            )}
          </div>
          <div className="lead-modal-header-right">
            {score != null && (
              <span className={`lead-modal-score lead-score-${scoreClass}`}>
                <i className="fas fa-star"></i> {score}/10
              </span>
            )}
            <button className="lead-modal-close-btn" onClick={onClose} aria-label="Close">
              <i className="fas fa-times"></i>
            </button>
          </div>
        </div>

        {/* ── Meta row ── */}
        <div className="lead-modal-meta-bar">
          {ins.hub_entity && (
            <div className="lead-modal-meta-item">
              <i className="fas fa-user-circle"></i>
              <span className="lead-meta-label">Hub Entity</span>
              <span className="lead-meta-value">{ins.hub_entity}</span>
            </div>
          )}
          {ins.hub_type && (
            <div className="lead-modal-meta-item">
              <i className="fas fa-tag"></i>
              <span className="lead-meta-label">Hub Type</span>
              <span className="lead-meta-value">{ins.hub_type}</span>
            </div>
          )}
          {ins.edge_count != null && (
            <div className="lead-modal-meta-item">
              <i className="fas fa-link"></i>
              <span className="lead-meta-label">Edge Count</span>
              <span className="lead-meta-value">{ins.edge_count}</span>
            </div>
          )}
          {ins.source_domains?.length > 0 && (
            <div className="lead-modal-meta-item">
              <i className="fas fa-database"></i>
              <span className="lead-meta-label">Sources</span>
              <span className="lead-meta-value">{ins.source_domains.join(', ')}</span>
            </div>
          )}
          {ins.generated_at && (
            <div className="lead-modal-meta-item">
              <i className="fas fa-clock"></i>
              <span className="lead-meta-label">Generated</span>
              <span className="lead-meta-value">
                {new Date(ins.generated_at).toLocaleString()}
              </span>
            </div>
          )}
        </div>

        {/* ── Body ── */}
        <div className="lead-modal-body">

          {/* Summary */}
          {ins.summary && (
            <div className="lead-modal-section">
              <h4 className="lead-modal-section-title">
                <i className="fas fa-file-alt"></i> Summary
              </h4>
              <p className="lead-modal-summary-text">{ins.summary}</p>
            </div>
          )}

          {/* Evidence */}
          {ins.evidence?.length > 0 && (
            <div className="lead-modal-section">
              <h4 className="lead-modal-section-title">
                <i className="fas fa-search"></i> Evidence
              </h4>
              <ul className="lead-modal-evidence-list">
                {ins.evidence.map((ev, i) => (
                  <li key={i} className="lead-modal-evidence-item">
                    <i className="fas fa-check-circle lead-evidence-icon"></i>
                    <span>{ev}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Recommended Action */}
          {ins.recommended_action && (
            <div className="lead-modal-section lead-modal-section--action">
              <h4 className="lead-modal-section-title">
                <i className="fas fa-bolt"></i> Recommended Action
              </h4>
              <p className="lead-modal-action-text">{ins.recommended_action}</p>
            </div>
          )}

          {/* Related Entities */}
          {entityIds.length > 0 && (
            <div className="lead-modal-section">
              <h4 className="lead-modal-section-title">
                <i className="fas fa-circle-nodes"></i> Related Entities
                <span className="lead-entities-count">{entityIds.length}</span>
              </h4>
              {loadingEntities ? (
                <div className="lead-entities-loading">
                  <i className="fas fa-spinner fa-spin"></i> Resolving entities…
                </div>
              ) : (
                <ul className="lead-modal-entity-list">
                  {entityIds.map((id) => {
                    const info = entityNames[id] || {};
                    const displayName = info.name || id;
                    const displayType = info.type || '—';
                    return (
                      <li key={id} className="lead-modal-entity-row">
                        <div className="lead-entity-info">
                          <span className="lead-entity-type-chip">{displayType}</span>
                          <span className="lead-entity-name">{displayName}</span>
                        </div>
                        <button
                          className="lead-entity-view-btn"
                          onClick={() => onOpenEntity(id)}
                          title={`Open entity context for ${displayName}`}
                        >
                          <i className="fas fa-arrow-right"></i> View Entity
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default GraphAnalytics;
